import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const PRIORITY_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#6b7280' };

function fmt(n) { return (n || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function TaxDashboard() {
  const { user, currentCompany } = useApp();
  const { t } = useI18n();
  const MONTHS = Array.from({length: 12}, (_, i) => t(`calendar.months.${i}`));
  const PRIORITY_LABELS = { high: `🔴 ${t('tax_dashboard.priority_high')}`, medium: `🟡 ${t('tax_dashboard.priority_medium')}`, low: `⚪ ${t('tax_dashboard.priority_low')}` };
  const [tab, setTab] = useState('overview');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [realtime, setRealtime] = useState(null);
  const [optimization, setOptimization] = useState(null);
  const [rzis, setRzis] = useState(null);
  const [bilans, setBilans] = useState(null);
  const [rzisVariant, setRzisVariant] = useState('porownawczy');
  const [periodType, setPeriodType] = useState('month');
  const [loading, setLoading] = useState(true);
  const [seedStatus, setSeedStatus] = useState('');
  const [formType, setFormType] = useState('standard');

  useEffect(() => { loadTab(); }, [tab, year, month, rzisVariant, periodType]);

  async function loadTab() {
    setLoading(true);
    try {
      if (tab === 'overview') {
        const cid = currentCompany?.id || user.company_id || 1;
        const [rt, opt] = await Promise.all([
          api.get(`/accounting/admin/realtime?year=${year}&month=${month}&period=${periodType}&company_id=${cid}`),
          api.get(`/accounting/admin/tax-optimization?year=${year}&company_id=${cid}`),
        ]);
        setRealtime(rt.data);
        setOptimization(opt.data);
      } else if (tab === 'rzis') {
        const { data } = await api.get(`/accounting/admin/reports/rzis-structured?company_id=${currentCompany?.id || user.company_id || 1}&year=${year}&month=${month}&period=${periodType}&variant=${rzisVariant}`);
        setRzis(data);
      } else if (tab === 'bilans') {
        const { data } = await api.get(`/accounting/admin/reports/bilans-structured?company_id=${currentCompany?.id || user.company_id || 1}&year=${year}&month=${month}&period=${periodType}`);
        setBilans(data);
      } else if (tab === 'optimization') {
        const { data } = await api.get(`/accounting/admin/tax-optimization?year=${year}&company_id=${currentCompany?.id || user.company_id || 1}`);
        setOptimization(data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function seedDefaults() {
    setSeedStatus('loading');
    try {
      const { data } = await api.post(`/accounting/admin/seed-defaults?company_id=${currentCompany?.id || user.company_id || 1}&form_type=${formType}`);
      setSeedStatus(`✅ Создано: ${data.accounts_created} счетов, ${data.rzis_mappings} маппингов RZiS, ${data.bilans_mappings} маппингов Bilans`);
    } catch (e) {
      setSeedStatus(`❌ ${e.response?.data?.detail || 'Ошибка'}`);
    }
  }

  async function downloadPdf(type) {
    const cid = currentCompany?.id || user.company_id || 1;
    let url;
    if (type === 'rzis') {
      url = `/export/rzis/pdf?company_id=${cid}&form_type=${formType}&year=${year}&month=${month}&period=${periodType}&variant=${rzisVariant}`;
    } else if (type === 'bilans') {
      url = `/export/bilans/pdf?company_id=${cid}&form_type=${formType}&year=${year}&month=${month}&period=${periodType}`;
    } else return;
    try {
      const resp = await api.get(url, { responseType: 'blob', timeout: 60000 });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${type}_${cid}_${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      alert(t('tax_dashboard.download_error') + ': ' + (e.response?.data?.detail || e.response?.status || e.message));
    }
  }

  const card = (label, value, color) => (
    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: 14, textAlign: 'center', flex: 1, minWidth: 100 }}>
      <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 'bold', color: color || '#fff' }}>{value}</div>
    </div>
  );

  return (
    <div className="page">
      <BackButton />
      <h1>🏦 {t('tax_dashboard.title')}</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {[
          { id: 'overview', label: `⚡ ${t('tax_dashboard.tab_overview')}` },
          { id: 'optimization', label: `🧠 ${t('tax_dashboard.tab_optimization')}` },
          { id: 'rzis', label: `📊 ${t('tax_dashboard.tab_rzis')}` },
          { id: 'bilans', label: `📋 ${t('tax_dashboard.tab_bilans')}` },
          { id: 'settings', label: `⚙️ ${t('tax_dashboard.tab_settings')}` },
        ].map(opt => (
          <button key={opt.id} className={`btn ${tab === opt.id ? 'btn-primary' : ''}`}
            style={{ background: tab === opt.id ? undefined : 'rgba(255,255,255,0.08)', fontSize: 13, padding: '8px 12px' }}
            onClick={() => setTab(opt.id)}>
            {opt.label}
          </button>
        ))}
      </div>

      {/* Company indicator */}
      {currentCompany && (
        <div style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 12, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>🏢</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>{currentCompany.short_name || currentCompany.name}</div>
            {currentCompany.nip && <div style={{ fontSize: 11, opacity: 0.5 }}>NIP: {currentCompany.nip}</div>}
          </div>
        </div>
      )}

      {/* Period Selector — prominent */}
      {tab !== 'settings' && (
        <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 14, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>📅 {t('tax_dashboard.period')}</div>
          {(tab === 'overview' || tab === 'rzis' || tab === 'bilans') && (
            <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
              {[{id:'month',label:t('tax_dashboard.period_month')},{id:'quarter',label:t('tax_dashboard.period_quarter')},{id:'year',label:t('tax_dashboard.period_year')}].map(p => (
                <button key={p.id} onClick={() => setPeriodType(p.id)}
                  style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: periodType === p.id ? '2px solid #6366f1' : '1px solid rgba(255,255,255,0.12)', background: periodType === p.id ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.06)', color: periodType === p.id ? '#a5b4fc' : 'rgba(255,255,255,0.6)', transition: 'all 0.15s' }}>
                  {p.label}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            {periodType !== 'year' && (
              <select value={month} onChange={e => setMonth(+e.target.value)}
                style={{ flex: 1, padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', fontSize: 15, fontWeight: 600 }}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            )}
            <div style={{ display: 'flex', gap: 4 }}>
              {[2024, 2025, 2026].map(y => (
                <button key={y} onClick={() => setYear(y)}
                  style={{ padding: '12px 16px', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', border: year === y ? '2px solid #6366f1' : '1px solid rgba(255,255,255,0.12)', background: year === y ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.06)', color: year === y ? '#a5b4fc' : 'rgba(255,255,255,0.6)', transition: 'all 0.15s' }}>
                  {y}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {loading && tab !== 'settings' ? <div className="loader">{t('common.loading')}</div> : (
        <>
          {/* === OVERVIEW === */}
          {tab === 'overview' && realtime && optimization && (
            <div>
              {/* Summary cards */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {card(t('tax_dashboard.vat_due'), `${fmt(realtime.vat?.due)} PLN`, realtime.vat?.due > 0 ? '#ef4444' : '#10b981')}
                {card(t('tax_dashboard.cit_due'), `${fmt(realtime.cit?.due)} PLN`, '#6366f1')}
                {card(t('tax_dashboard.pit_due'), `${fmt(realtime.pit?.due)} PLN`, '#f59e0b')}
                {card(t('tax_dashboard.effective_rate'), `${optimization.effective_tax_rate}%`, '#8b5cf6')}
              </div>

              {/* Revenue / Costs */}
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>📈 {t('tax_dashboard.finances')} ({periodType === 'year' ? year : periodType === 'quarter' ? `Q${Math.ceil(month/3)} ${year}` : `${MONTHS[month - 1]} ${year}`})</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <div>{t('tax_dashboard.turnover')}: <b style={{ color: '#10b981' }}>{fmt(realtime.turnover)} PLN</b></div>
                    {realtime.currency_breakdown?.EUR?.income > 0 && (
                      <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>{t('tax_dashboard.incl_eur')}: {fmt(realtime.currency_breakdown.EUR.income)} €</div>
                    )}
                  </div>
                  <div>
                    <div>{t('tax_dashboard.expenses')}: <b style={{ color: '#ef4444' }}>{fmt(realtime.expenses)} PLN</b></div>
                    {realtime.currency_breakdown?.EUR?.expense > 0 && (
                      <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>{t('tax_dashboard.incl_eur')}: {fmt(realtime.currency_breakdown.EUR.expense)} €</div>
                    )}
                  </div>
                </div>
                {/* Currency breakdown */}
                {realtime.currency_breakdown && Object.keys(realtime.currency_breakdown).length > 1 && (
                  <div style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(99,102,241,0.08)', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4 }}>💱 {t('tax_dashboard.currency_breakdown')}</div>
                    {Object.entries(realtime.currency_breakdown).map(([cur, data]) => (
                      <div key={cur} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0' }}>
                        <span style={{ fontWeight: 600 }}>{cur}</span>
                        <span>
                          <span style={{ color: '#10b981' }}>+{fmt(data.income)}</span>
                          {' / '}
                          <span style={{ color: '#ef4444' }}>-{fmt(data.expense)}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Form comparison */}
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 10 }}>📊 {t('tax_dashboard.form_comparison')} ({year})</div>
                {optimization.comparison?.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0',
                    borderBottom: i < optimization.comparison.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                    <div style={{ fontSize: 14 }}>{c.form}</div>
                    <div>
                      <span style={{ fontWeight: 'bold' }}>{fmt(c.total_tax)} PLN</span>
                      <span style={{ fontSize: 12, opacity: 0.6, marginLeft: 6 }}>({c.effective_rate}%)</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* ZUS options */}
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 10 }}>🏥 {t('tax_dashboard.zus_options')}</div>
                {optimization.zus_options?.map((z, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0',
                    borderBottom: i < optimization.zus_options.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                    <div>{z.name}</div>
                    <div><b>{fmt(z.monthly)}</b>{t('tax_dashboard.per_month')} · <span style={{ opacity: 0.7 }}>{fmt(z.yearly)}{t('tax_dashboard.per_year')}</span></div>
                  </div>
                ))}
                <div style={{ marginTop: 8, fontSize: 12, opacity: 0.6 }}>
                  Składka zdrowotna: skala 9% = {fmt(optimization.zdrowotna?.skala_9pct)}{t('tax_dashboard.per_month')}, liniowy 4.9% = {fmt(optimization.zdrowotna?.liniowy_4_9pct)}{t('tax_dashboard.per_month')}
                </div>
              </div>

              {/* Top recommendations */}
              {optimization.recommendations?.length > 0 && (
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: 10 }}>🧠 {t('tax_dashboard.top_recommendations')}</div>
                  {optimization.recommendations.slice(0, 3).map((r, i) => (
                    <div key={i} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 12, marginBottom: 8,
                      borderLeft: `3px solid ${PRIORITY_COLORS[r.priority]}` }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{r.title}</div>
                      <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 4 }}>{r.description}</div>
                      {r.potential_saving > 0 && (
                        <div style={{ fontSize: 12, color: '#10b981' }}>💰 {t('tax_dashboard.potential_saving')}: {fmt(r.potential_saving)} PLN</div>
                      )}
                    </div>
                  ))}
                  <button className="btn" onClick={() => setTab('optimization')} style={{ marginTop: 8, fontSize: 13 }}>
                    {t('tax_dashboard.all_recommendations')} →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* === OPTIMIZATION === */}
          {tab === 'optimization' && optimization && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {card(t('tax_dashboard.revenue_year'), `${fmt(optimization.revenue)} PLN`, '#10b981')}
                {card(t('tax_dashboard.expenses_year'), `${fmt(optimization.costs)} PLN`, '#ef4444')}
                {card(t('tax_dashboard.profit'), `${fmt(optimization.profit)} PLN`, '#6366f1')}
                {card(t('tax_dashboard.total_taxes'), `${fmt(optimization.current_tax_burden?.total)} PLN`, '#f59e0b')}
              </div>

              <h3 style={{ marginBottom: 12 }}>📋 {t('tax_dashboard.all_recommendations')}</h3>
              {optimization.recommendations?.map((r, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 14, marginBottom: 8,
                  borderLeft: `3px solid ${PRIORITY_COLORS[r.priority]}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 6 }}>
                    <div style={{ fontWeight: 'bold' }}>{r.title}</div>
                    <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{PRIORITY_LABELS[r.priority]}</span>
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>{r.description}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ opacity: 0.5 }}>{t('tax_dashboard.category')}: {r.category?.toUpperCase()}</span>
                    {r.potential_saving > 0 && (
                      <span style={{ color: '#10b981', fontWeight: 'bold' }}>💰 {t('tax_dashboard.saving')}: {fmt(r.potential_saving)} PLN</span>
                    )}
                  </div>
                </div>
              ))}

              <h3 style={{ marginTop: 20, marginBottom: 12 }}>📊 {t('tax_dashboard.form_comparison')}</h3>
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16 }}>
                {optimization.comparison?.map((c, i) => {
                  const best = optimization.comparison.reduce((a, b) => a.total_tax < b.total_tax ? a : b);
                  const isBest = c.form === best.form;
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                      borderBottom: i < optimization.comparison.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                      background: isBest ? 'rgba(16,185,129,0.1)' : 'transparent', borderRadius: isBest ? 8 : 0, padding: isBest ? '10px 8px' : '10px 0' }}>
                      <div>
                        <div style={{ fontWeight: isBest ? 'bold' : 'normal' }}>{isBest ? '✅ ' : ''}{c.form}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 'bold' }}>{fmt(c.total_tax)} PLN</div>
                        <div style={{ fontSize: 12, opacity: 0.6 }}>{t('tax_dashboard.effective_rate')}: {c.effective_rate}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* === RZiS === */}
          {tab === 'rzis' && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <button className={`btn ${rzisVariant === 'porownawczy' ? 'btn-primary' : ''}`}
                  style={{ fontSize: 12, padding: '6px 10px', background: rzisVariant === 'porownawczy' ? undefined : 'rgba(255,255,255,0.08)' }}
                  onClick={() => setRzisVariant('porownawczy')}>Porównawczy</button>
                <button className={`btn ${rzisVariant === 'kalkulacyjny' ? 'btn-primary' : ''}`}
                  style={{ fontSize: 12, padding: '6px 10px', background: rzisVariant === 'kalkulacyjny' ? undefined : 'rgba(255,255,255,0.08)' }}
                  onClick={() => setRzisVariant('kalkulacyjny')}>Kalkulacyjny</button>
                <button className="btn" style={{ fontSize: 12, padding: '6px 10px', marginLeft: 'auto' }} onClick={() => downloadPdf('rzis')}>📥 PDF</button>
              </div>

              {rzis ? (
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16 }}>
                  <h3 style={{ marginBottom: 12 }}>Rachunek Zysków i Strat ({rzisVariant})</h3>
                  <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 8 }}>
                    {rzis.period?.date_from} — {rzis.period?.date_to}
                  </div>
                  {rzis.data_source === 'accounting_entries' && (
                    <div style={{ fontSize: 11, color: '#f59e0b', marginBottom: 12, padding: '4px 8px', background: 'rgba(245,158,11,0.1)', borderRadius: 6, display: 'inline-block' }}>
                      ⚠️ Dane z uproszczonych zapisów księgowych
                    </div>
                  )}
                  {rzis.lines?.map((l, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', padding: `6px ${l.level * 16}px 6px ${l.level * 20 + 4}px`,
                      fontWeight: l.level === 0 ? 'bold' : 'normal',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      background: l.level === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
                      fontSize: l.level === 0 ? 14 : 13,
                    }}>
                      <div><span style={{ opacity: 0.5, marginRight: 6 }}>{l.code}</span>{l.name}</div>
                      <div style={{ whiteSpace: 'nowrap', color: l.amount < 0 ? '#ef4444' : l.amount > 0 ? '#10b981' : '#fff' }}>
                        {fmt(l.amount)} PLN
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div style={{ opacity: 0.5, textAlign: 'center', padding: 32 }}>{t('tax_dashboard.no_data')}</div>}
            </div>
          )}

          {/* === BILANS === */}
          {tab === 'bilans' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
                <button className="btn" style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => downloadPdf('bilans')}>📥 PDF</button>
              </div>

              {bilans ? (
                <>
                  <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                    <h3 style={{ marginBottom: 12 }}>AKTYWA</h3>
                    {bilans.aktywa?.map((l, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', padding: `6px 0 6px ${l.level * 20}px`,
                        fontWeight: l.level === 0 ? 'bold' : 'normal',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        fontSize: l.level === 0 ? 14 : 13,
                      }}>
                        <div><span style={{ opacity: 0.5, marginRight: 6 }}>{l.code}</span>{l.name}</div>
                        <div style={{ whiteSpace: 'nowrap' }}>{fmt(l.amount)} PLN</div>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 'bold', fontSize: 16,
                      borderTop: '2px solid rgba(255,255,255,0.2)', marginTop: 8 }}>
                      <div>RAZEM AKTYWA</div>
                      <div>{fmt(bilans.total_aktywa)} PLN</div>
                    </div>
                  </div>

                  <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                    <h3 style={{ marginBottom: 12 }}>PASYWA</h3>
                    {bilans.pasywa?.map((l, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', padding: `6px 0 6px ${l.level * 20}px`,
                        fontWeight: l.level === 0 ? 'bold' : 'normal',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        fontSize: l.level === 0 ? 14 : 13,
                      }}>
                        <div><span style={{ opacity: 0.5, marginRight: 6 }}>{l.code}</span>{l.name}</div>
                        <div style={{ whiteSpace: 'nowrap' }}>{fmt(l.amount)} PLN</div>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 'bold', fontSize: 16,
                      borderTop: '2px solid rgba(255,255,255,0.2)', marginTop: 8 }}>
                      <div>RAZEM PASYWA</div>
                      <div>{fmt(bilans.total_pasywa)} PLN</div>
                    </div>
                  </div>

                  <div style={{ textAlign: 'center', padding: 12, borderRadius: 12,
                    background: bilans.balanced ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)' }}>
                    {bilans.balanced ? '✅ Bilans się zgadza' : `⚠️ Różnica: ${fmt(bilans.total_aktywa - bilans.total_pasywa)} PLN`}
                  </div>
                </>
              ) : <div style={{ opacity: 0.5, textAlign: 'center', padding: 32 }}>{t('tax_dashboard.no_data')}</div>}
            </div>
          )}

          {/* === SETTINGS === */}
          {tab === 'settings' && (
            <div>
              <h3 style={{ marginBottom: 16 }}>⚙️ {t('tax_dashboard.report_settings')}</h3>

              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 12 }}>{t('tax_dashboard.report_type')}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['standard', 'small', 'micro'].map(ft => (
                    <button key={ft} className={`btn ${formType === ft ? 'btn-primary' : ''}`}
                      style={{ background: formType === ft ? undefined : 'rgba(255,255,255,0.08)', fontSize: 13, padding: '8px 14px' }}
                      onClick={() => setFormType(ft)}>
                      {ft === 'standard' ? `📊 ${t('tax_dashboard.standard')}` : ft === 'small' ? '📋 Małe' : '📝 Mikro'}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>🌱 {t('tax_dashboard.seed_chart')}</div>
                <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 12 }}>
                  {t('tax_dashboard.seed_desc')}
                </div>
                <button className="btn btn-primary" onClick={seedDefaults} disabled={seedStatus === 'loading'}>
                  {seedStatus === 'loading' ? `⏳ ${t('tax_dashboard.seeding')}` : `🌱 ${t('tax_dashboard.seed_btn')}`}
                </button>
                {seedStatus && seedStatus !== 'loading' && (
                  <div style={{ marginTop: 10, fontSize: 13, padding: 10, borderRadius: 8,
                    background: seedStatus.startsWith('✅') ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)' }}>
                    {seedStatus}
                  </div>
                )}
              </div>

              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>📥 {t('tax_dashboard.download_reports')}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn" style={{ fontSize: 13 }} onClick={() => downloadPdf('rzis')}>📊 RZiS (PDF)</button>
                  <button className="btn" style={{ fontSize: 13 }} onClick={() => downloadPdf('bilans')}>📋 Bilans (PDF)</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
